import type { AxiosRequestConfig } from "axios";
import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  CollectionResponse,
  Concern,
  Contract,
  ContractRate,
  ContractRatePayload,
  FilterByQuery,
  IncludeQuery,
  Insurer,
  InsurerNative,
  InsurerPackage,
  InsurerRatesPayload,
  EmptyResponse,
  PaginationQuery,
  Rate,
  Response,
  SortByQuery,
  UpdateInsurerContractPositionsPayload,
} from "~/types/api";

export function createInsurerApi({ getRequest, postRequest, putRequest, getPaginationRequest }: ApiRequestHelpers) {
  /** Fetches all insurers. */
  const insurers = () => getRequest<CollectionResponse<Insurer>>(`/insurers`);

  /** Fetches all native (system-defined) insurers. */
  const insurersNative = () => getRequest<CollectionResponse<InsurerNative>>(`/insurers/native`);

  /** Fetches a paginated list of supplementary insurer packages. */
  const insurerPackages = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => {
    const config: AxiosRequestConfig = {
      params: {
        "types[]": ["Aanvullend", "AanvullendPlusTand"],
      },
    };
    return getPaginationRequest<InsurerPackage>(`/insurers/packages`, pagination, sortBy, includes, filterBy, config);
  };

  /** Updates the annual covered treatments limit for an insurer package. */
  const insurerPackagesChangeAnnualCoveredTreatments = (
    insurerPackageId: string,
    annualCoveredTreatments: number | null,
  ) =>
    postRequest<EmptyResponse>(`/insurers/packages/${insurerPackageId}/treatments`, {
      annual_covered_treatments: annualCoveredTreatments,
    });

  /** Fetches a paginated list of insurer contract positions for a given year. */
  const getInsurerContractPositions = (pagination: PaginationQuery, sortBy?: SortByQuery, year?: number) =>
    getPaginationRequest<Concern>(`/insurers/contracts`, pagination, sortBy, undefined, undefined, {
      params: { year: year ?? new Date().getFullYear() },
    });

  /** Fetches a single insurer's contract position for a given year. */
  const insurerContractPosition = (insurerRelation: Insurer, year?: number) =>
    getRequest<Insurer>(`/insurers/contracts/${insurerRelation}`, {
      params: { year: year ?? new Date().getFullYear() },
    });

  /** Updates insurer contract positions in bulk. */
  const updateInsurerContractPositions = (data: UpdateInsurerContractPositionsPayload) =>
    putRequest<Response<Insurer>>(`/insurers/contracts`, data);

  /** Fetches all insurer concerns (groups of insurers). */
  const concerns = () => getRequest<Response<Concern[]>>(`/insurers/concerns`);

  /** Creates a new concern with linked insurers. */
  const storeConcern = (data: { name: string; insurer_ids: number[] }) =>
    postRequest<Response<{ id: number }>>(`/insurers/concerns`, data);

  /** Updates an existing concern's name and optionally its linked insurers. */
  const updateConcern = (concernId: number, data: { name: string; insurer_ids?: number[] }) =>
    postRequest<Response<void>>(`/insurers/concerns/${concernId}`, data);

  /** Fetches all contracts belonging to a concern. */
  const concernContracts = (concernId: number) =>
    getRequest<Response<Contract[]>>(`/insurers/concerns/${concernId}/contracts`);

  /** Creates a new contract under a concern. */
  const storeConcernContract = (concernId: number, data: { name: string }) =>
    postRequest<Response<{ id: number }>>(`/insurers/concerns/${concernId}/contracts`, data);

  /** Updates an existing contract under a concern. */
  const updateConcernContract = (concernId: number, contractId: number, data: { name: string }) =>
    postRequest<Response<void>>(`/insurers/concerns/${concernId}/contracts/${contractId}`, data);

  /** Fetches all rates for a specific concern contract. */
  const getContractRates = (concernId: number, contractId: number) =>
    getRequest<Response<ContractRate[]>>(`/insurers/concerns/${concernId}/contracts/${contractId}/rates`);

  /** Creates a new rate for a concern contract. */
  const storeContractRate = (concernId: number, contractId: number, data: ContractRatePayload) =>
    postRequest<Response<void>>(`/insurers/concerns/${concernId}/contracts/${contractId}/rates`, data);

  /** Updates an existing rate for a concern contract. */
  const updateContractRate = (concernId: number, contractId: number, rateId: number, data: ContractRatePayload) =>
    postRequest<Response<void>>(`/insurers/concerns/${concernId}/contracts/${contractId}/rates/${rateId}`, data);

  /** Fetches a paginated list of insurer rates. */
  const insurersRates = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => getPaginationRequest<Rate>("/insurers/rates", pagination, sortBy, includes, filterBy);

  /** Updates insurer rates in bulk. */
  const updateInsurerRates = (data: InsurerRatesPayload[]) => postRequest<Response<Insurer>>(`/insurers/rates`, data);

  return {
    insurers,
    insurersNative,
    insurerPackages,
    insurerPackagesChangeAnnualCoveredTreatments,
    getInsurerContractPositions,
    insurerContractPosition,
    updateInsurerContractPositions,
    concerns,
    storeConcern,
    updateConcern,
    concernContracts,
    storeConcernContract,
    updateConcernContract,
    getContractRates,
    storeContractRate,
    updateContractRate,
    insurersRates,
    updateInsurerRates,
  };
}

export type InsurerApi = ReturnType<typeof createInsurerApi>;
